// src/pages/ExpenseTracker.jsx — Expense & Income Tracker
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../utils/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
];

const currencySymbol = (code) => {
  const c = CURRENCIES.find(x => x.code === (code || 'INR').toUpperCase());
  return c ? c.symbol : (code || '₹');
};

const fmtAmount = (v, currency) => {
  const sym = currencySymbol(currency);
  const locale = (currency || 'INR') === 'INR' ? 'en-IN' : 'en-US';
  return sym + Number(v || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Keep backward compat for places not yet updated
const fmtINR = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().split('T')[0];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CATEGORY_COLORS = [
  '#4caf50', '#2196f3', '#f44336', '#ff9800', '#9c27b0',
  '#00bcd4', '#607d8b', '#e91e63', '#795548', '#009688',
];

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page: { padding: '24px', maxWidth: '1200px', margin: '0 auto', color: '#e0e0e0' },
  h1: { fontSize: '22px', fontWeight: 700, marginBottom: '4px', color: '#fff' },
  subtitle: { fontSize: '13px', color: '#888', marginBottom: '24px' },

  // Summary cards
  cardRow: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  card: {
    flex: '1 1 180px', background: '#1e1e2e', borderRadius: '10px',
    padding: '18px 20px', border: '1px solid #2a2a3e',
  },
  cardLabel: { fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: '24px', fontWeight: 700 },

  // Section headers
  sectionHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '12px', cursor: 'pointer', userSelect: 'none',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#ccc' },
  section: {
    background: '#1e1e2e', borderRadius: '10px', border: '1px solid #2a2a3e',
    padding: '18px 20px', marginBottom: '20px',
  },

  // Filters
  filterRow: { display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  select: {
    background: '#16162a', border: '1px solid #333', borderRadius: '6px',
    color: '#ddd', padding: '6px 10px', fontSize: '13px', cursor: 'pointer',
  },
  btnPrimary: {
    background: '#3a3aff', color: '#fff', border: 'none', borderRadius: '6px',
    padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
  },
  btnSecondary: {
    background: '#2a2a3e', color: '#ccc', border: '1px solid #333', borderRadius: '6px',
    padding: '7px 14px', fontSize: '13px', cursor: 'pointer',
  },
  btnDanger: {
    background: 'transparent', color: '#f44336', border: '1px solid #f44336',
    borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
  },
  btnSmall: {
    background: '#2a2a3e', color: '#aaa', border: '1px solid #333', borderRadius: '5px',
    padding: '3px 9px', fontSize: '12px', cursor: 'pointer',
  },

  // Input
  input: {
    background: '#16162a', border: '1px solid #333', borderRadius: '6px',
    color: '#ddd', padding: '6px 10px', fontSize: '13px', width: '100%', boxSizing: 'border-box',
  },

  // Table
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: '1px solid #2a2a3e', padding: '8px 10px',
  },
  td: { padding: '8px 10px', borderBottom: '1px solid #1a1a2a', verticalAlign: 'middle' },
  trMisc: { background: 'rgba(255,193,7,0.07)' },

  // Badge
  badgeIncome: {
    background: 'rgba(76,175,80,0.15)', color: '#4caf50', borderRadius: '4px',
    padding: '2px 8px', fontSize: '11px', fontWeight: 600,
  },
  badgeExpense: {
    background: 'rgba(244,67,54,0.15)', color: '#f44336', borderRadius: '4px',
    padding: '2px 8px', fontSize: '11px', fontWeight: 600,
  },
  badgeMisc: {
    background: 'rgba(255,193,7,0.15)', color: '#ffc107', borderRadius: '4px',
    padding: '2px 7px', fontSize: '10px', fontWeight: 600, marginLeft: '4px',
  },
  badgeGlobal: {
    background: 'rgba(100,181,246,0.12)', color: '#64b5f6', borderRadius: '4px',
    padding: '2px 7px', fontSize: '10px', fontWeight: 600,
  },

  // Flash
  flash: (type) => ({
    background: type === 'error' ? 'rgba(244,67,54,0.15)' : 'rgba(76,175,80,0.15)',
    color: type === 'error' ? '#f44336' : '#4caf50',
    border: `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`,
    borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px',
  }),

  // Dot indicator
  dot: (color) => ({
    display: 'inline-block', width: '8px', height: '8px',
    borderRadius: '50%', background: color, marginRight: '6px',
  }),
};

// ── Flash message component ───────────────────────────────────────────────────

function Flash({ msg, onDismiss }) {
  if (!msg) return null;
  return <div style={S.flash(msg.type)}>{msg.text} <span style={{ float: 'right', cursor: 'pointer' }} onClick={onDismiss}>✕</span></div>;
}

// ── Summary cards ─────────────────────────────────────────────────────────────

function ConvertedValueWithTooltip({ value, currency, breakdown, ratesUsed, label }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-block', cursor: 'help' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span>{fmtAmount(value, currency)}</span>
      <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '4px' }}>ⓘ</span>
      {hover && breakdown && breakdown.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          background: '#0f172a', border: '1px solid #334155', borderRadius: '8px',
          padding: '10px 14px', zIndex: 999, minWidth: '240px',
          fontSize: '12px', color: '#cbd5e1', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          whiteSpace: 'nowrap', marginBottom: '6px',
        }}>
          <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: '6px' }}>{label} breakdown</div>
          {breakdown.map(b => {
            const rate = ratesUsed ? ratesUsed[b.currency] : null;
            const amt = label === 'Income' ? b.income : label === 'Expense' ? b.expense : b.savings;
            const converted = rate ? amt / rate : amt;
            return (
              <div key={b.currency} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
                <span style={{ color: '#94a3b8' }}>
                  {fmtAmount(amt, b.currency)}
                  {rate && rate !== 1 ? <span style={{ color: '#64748b' }}> ÷ {rate.toFixed(4)}</span> : null}
                </span>
                <span style={{ color: '#38bdf8' }}>= {fmtAmount(converted, currency)}</span>
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid #1e293b', marginTop: '6px', paddingTop: '6px', color: '#e2e8f0', fontWeight: 600 }}>
            Total: {fmtAmount(value, currency)}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCards({ summary, openingBalance, currencyFilter, baseCurrency, setBaseCurrency }) {
  const breakdown = summary.breakdown || [];
  const isMultiCurrency = breakdown.length > 1;
  const hasConverted = !!summary.base_currency;
  const ratesUsed = summary.rates_used || null;
  const savingsColor = (hasConverted ? summary.converted_savings : summary.savings) >= 0 ? '#4caf50' : '#f44336';

  const CURRENCY_PILLS = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'SGD'];

  const CurrencySelector = () => (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>CONVERT TO:</span>
      {CURRENCY_PILLS.map(c => (
        <button
          key={c}
          onClick={() => setBaseCurrency(c)}
          style={{
            padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer', border: '1px solid',
            background: baseCurrency === c ? '#0c4a6e' : 'transparent',
            borderColor: baseCurrency === c ? '#38bdf8' : '#334155',
            color: baseCurrency === c ? '#38bdf8' : '#64748b',
          }}
        >
          {c}
        </button>
      ))}
    </div>
  );

  // When a single currency is filtered, show simple 5-card layout
  if (currencyFilter && currencyFilter !== 'all') {
    const sym = currencySymbol(currencyFilter);
    const fmt = (v) => sym + fmtINR(v);
    return (
      <>
        {isMultiCurrency && <CurrencySelector />}
        <div style={S.cardRow}>
          <div style={S.card}>
            <div style={S.cardLabel}>Opening Balance</div>
            <div style={{ ...S.cardValue, color: '#64b5f6' }}>{fmt(openingBalance)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Total Income</div>
            <div style={{ ...S.cardValue, color: '#4caf50' }}>{fmt(summary.total_income)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Total Expenses</div>
            <div style={{ ...S.cardValue, color: '#f44336' }}>{fmt(summary.total_expense)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Net Savings</div>
            <div style={{ ...S.cardValue, color: savingsColor }}>{fmt(summary.savings)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Closing Balance</div>
            <div style={{ ...S.cardValue, color: '#fff' }}>{fmt(openingBalance + summary.savings)}</div>
          </div>
        </div>
      </>
    );
  }

  // Multi-currency: show per-currency breakdown + converted totals
  if (isMultiCurrency) {
    return (
      <>
        <CurrencySelector />
        {/* Per-currency mini cards */}
        <div style={S.cardRow}>
          {breakdown.map(b => (
            <div key={b.currency} style={S.card}>
              <div style={{ ...S.cardLabel, display: 'flex', justifyContent: 'space-between' }}>
                <span>{b.currency}</span>
                <span style={{ color: b.savings >= 0 ? '#4caf50' : '#f44336', fontSize: '11px' }}>
                  {b.savings >= 0 ? '+' : ''}{fmtAmount(b.savings, b.currency)} net
                </span>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '13px' }}>
                <span style={{ color: '#4caf50' }}>↑ {fmtAmount(b.income, b.currency)}</span>
                <span style={{ color: '#f44336' }}>↓ {fmtAmount(b.expense, b.currency)}</span>
              </div>
            </div>
          ))}
        </div>
        {/* Converted totals row */}
        {hasConverted ? (
          <>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', letterSpacing: '0.05em' }}>
              CONVERTED TOTALS ({summary.base_currency}) — hover values for breakdown
            </div>
            <div style={S.cardRow}>
              <div style={S.card}>
                <div style={S.cardLabel}>Total Income</div>
                <div style={{ ...S.cardValue, color: '#4caf50' }}>
                  <ConvertedValueWithTooltip value={summary.converted_total_income} currency={summary.base_currency} breakdown={breakdown} ratesUsed={ratesUsed} label="Income" />
                </div>
              </div>
              <div style={S.card}>
                <div style={S.cardLabel}>Total Expenses</div>
                <div style={{ ...S.cardValue, color: '#f44336' }}>
                  <ConvertedValueWithTooltip value={summary.converted_total_expense} currency={summary.base_currency} breakdown={breakdown} ratesUsed={ratesUsed} label="Expense" />
                </div>
              </div>
              <div style={S.card}>
                <div style={S.cardLabel}>Net Savings</div>
                <div style={{ ...S.cardValue, color: savingsColor }}>
                  <ConvertedValueWithTooltip value={summary.converted_savings} currency={summary.base_currency} breakdown={breakdown} ratesUsed={ratesUsed} label="Savings" />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#0c2a1e', border: '1px solid #1a4a30', fontSize: '12px', color: '#86efac', marginBottom: '12px' }}>
            Multiple currencies detected — select a base currency above to see converted totals.
          </div>
        )}
      </>
    );
  }

  // Single currency (default)
  const activeCurrency = breakdown.length === 1 ? breakdown[0].currency : 'INR';
  const sym = currencySymbol(activeCurrency);
  const fmt = (v) => sym + fmtINR(v);
  return (
    <div style={S.cardRow}>
      <div style={S.card}>
        <div style={S.cardLabel}>Opening Balance</div>
        <div style={{ ...S.cardValue, color: '#64b5f6' }}>{fmt(openingBalance)}</div>
      </div>
      <div style={S.card}>
        <div style={S.cardLabel}>Total Income</div>
        <div style={{ ...S.cardValue, color: '#4caf50' }}>{fmt(summary.total_income)}</div>
      </div>
      <div style={S.card}>
        <div style={S.cardLabel}>Total Expenses</div>
        <div style={{ ...S.cardValue, color: '#f44336' }}>{fmt(summary.total_expense)}</div>
      </div>
      <div style={S.card}>
        <div style={S.cardLabel}>Net Savings</div>
        <div style={{ ...S.cardValue, color: savingsColor }}>{fmt(summary.savings)}</div>
      </div>
      <div style={S.card}>
        <div style={S.cardLabel}>Closing Balance</div>
        <div style={{ ...S.cardValue, color: '#fff' }}>{fmt(openingBalance + summary.savings)}</div>
      </div>
    </div>
  );
}

// ── Category management ───────────────────────────────────────────────────────

function CategoryList({ label, list, isGlobal, onDelete }) {
  return (
    <div style={{ flex: '1 1 220px' }}>
      <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textTransform: 'uppercase' }}>
        {label}
      </div>
      {list.length === 0 && <div style={{ color: '#555', fontSize: '13px' }}>None yet</div>}
      {list.map(cat => (
        <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={S.dot(cat.color)} />
          <span style={{ fontSize: '13px', flex: 1 }}>{cat.name}</span>
          {isGlobal
            ? <span style={S.badgeGlobal}>Global</span>
            : <button style={S.btnDanger} onClick={() => onDelete(cat.id)}>✕</button>
          }
        </div>
      ))}
    </div>
  );
}

function CategoryManager({ categories, onAdd, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'expense', color: CATEGORY_COLORS[0] });
  const [adding, setAdding] = useState(false);

  const globalCats = categories.filter(c => c.is_global);
  const userCats   = categories.filter(c => !c.is_global);

  const globalIncome  = globalCats.filter(c => c.type === 'income');
  const globalExpense = globalCats.filter(c => c.type === 'expense');
  const userIncome    = userCats.filter(c => c.type === 'income');
  const userExpense   = userCats.filter(c => c.type === 'expense');

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    await onAdd(form);
    setForm({ name: '', type: 'expense', color: CATEGORY_COLORS[0] });
    setAdding(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Categories</span>
        <span style={{ color: '#666', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          {/* Add your own category */}
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Add Your Category
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <input
              style={{ ...S.input, flex: '1 1 140px' }}
              placeholder="Category name"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <select
              style={S.select}
              value={form.type}
              onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
            >
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {CATEGORY_COLORS.map(c => (
                <div
                  key={c}
                  onClick={() => setForm(p => ({ ...p, color: c }))}
                  style={{
                    width: '16px', height: '16px', borderRadius: '50%', background: c, cursor: 'pointer',
                    border: form.color === c ? '2px solid #fff' : '2px solid transparent',
                  }}
                />
              ))}
            </div>
            <button style={S.btnPrimary} onClick={handleAdd} disabled={adding}>
              {adding ? 'Adding…' : '+ Add'}
            </button>
          </div>

          {/* Your categories */}
          {(userIncome.length > 0 || userExpense.length > 0) && (
            <>
              <div style={{ fontSize: '12px', color: '#64b5f6', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Your Categories
              </div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <CategoryList label="Income"  list={userIncome}  isGlobal={false} onDelete={onDelete} />
                <CategoryList label="Expense" list={userExpense} isGlobal={false} onDelete={onDelete} />
              </div>
            </>
          )}

          {/* Global (shared) categories */}
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Global Categories <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>(shared · read-only)</span>
          </div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <CategoryList label="Income"  list={globalIncome}  isGlobal={true} onDelete={onDelete} />
            <CategoryList label="Expense" list={globalExpense} isGlobal={true} onDelete={onDelete} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Opening balance manager ───────────────────────────────────────────────────

function BalanceManager({ balances, onSave, onDelete }) {
  const [open, setOpen]   = useState(false);
  const [form, setForm]   = useState({ bank_name: '', balance: '', currency: 'INR', as_of_date: today(), notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.bank_name.trim() || !form.balance) return;
    setSaving(true);
    await onSave(form);
    setForm({ bank_name: '', balance: '', currency: 'INR', as_of_date: today(), notes: '' });
    setSaving(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Bank Balances (Opening Balance)</span>
        <span style={{ color: '#666', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input style={{ ...S.input, flex: '1 1 140px' }} placeholder="Bank / Account name"
              value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} />
            <input style={{ ...S.input, flex: '0 0 130px' }} type="number" placeholder="Balance"
              value={form.balance} onChange={e => setForm(p => ({ ...p, balance: e.target.value }))} />
            <select style={S.select} value={form.currency}
              onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>)}
            </select>
            <input style={{ ...S.input, flex: '0 0 140px' }} type="date"
              value={form.as_of_date} onChange={e => setForm(p => ({ ...p, as_of_date: e.target.value }))} />
            <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {balances.length === 0 && <div style={{ color: '#555', fontSize: '13px' }}>No bank balances added yet.</div>}
          {balances.map(b => (
            <div key={b.id} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '6px', fontSize: '13px' }}>
              <span style={{ flex: 1 }}>{b.bank_name}</span>
              <span style={{ color: '#64b5f6' }}>{fmtAmount(b.balance, b.currency)}</span>
              <span style={{ color: '#888', fontSize: '11px' }}>{b.currency || 'INR'}</span>
              <span style={{ color: '#555' }}>{b.as_of_date}</span>
              <button style={S.btnDanger} onClick={() => onDelete(b.id)}>✕</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Add transaction form ──────────────────────────────────────────────────────

function AddTransactionForm({ categories, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: today(), description: '', amount: '', currency: 'INR', type: 'expense',
    category_id: '', category_name: '', notes: '',
  });
  const [adding, setAdding] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const filtered       = categories.filter(c => c.type === form.type);
  const filteredGlobal = filtered.filter(c => c.is_global);
  const filteredUser   = filtered.filter(c => !c.is_global);

  const handleAdd = async () => {
    if (!form.description.trim() || !form.amount || !form.date) return;
    setAdding(true);
    const cat = categories.find(c => c.id === parseInt(form.category_id));
    await onAdd({
      ...form,
      amount: parseFloat(form.amount),
      category_id: cat ? cat.id : null,
      category_name: cat ? cat.name : (form.category_name || 'Miscellaneous'),
      is_misc: !cat,
      source: 'manual',
    });
    setForm({ date: today(), description: '', amount: '', currency: 'INR', type: 'expense', category_id: '', category_name: '', notes: '' });
    setAdding(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Add Transaction</span>
        <span style={{ color: '#666', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input style={{ ...S.input, flex: '0 0 140px' }} type="date"
            value={form.date} onChange={e => set('date', e.target.value)} />
          <input style={{ ...S.input, flex: '2 1 200px' }} placeholder="Description *"
            value={form.description} onChange={e => set('description', e.target.value)} />
          <input style={{ ...S.input, flex: '0 0 120px' }} type="number" placeholder="Amount *"
            value={form.amount} onChange={e => set('amount', e.target.value)} />
          <select style={S.select} value={form.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>)}
          </select>
          <select style={S.select} value={form.type} onChange={e => { set('type', e.target.value); set('category_id', ''); }}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
          <select style={S.select} value={form.category_id}
            onChange={e => set('category_id', e.target.value)}>
            <option value="">— Category —</option>
            {filteredUser.length > 0 && (
              <optgroup label="Your Categories">
                {filteredUser.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
            {filteredGlobal.length > 0 && (
              <optgroup label="Global Categories">
                {filteredGlobal.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
          </select>
          <input style={{ ...S.input, flex: '1 1 160px' }} placeholder="Notes (optional)"
            value={form.notes} onChange={e => set('notes', e.target.value)} />
          <button style={S.btnPrimary} onClick={handleAdd} disabled={adding}>
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Bank statement uploader ───────────────────────────────────────────────────

function StatementUploader({ categories, onImport }) {
  const [open, setOpen]                   = useState(false);
  const [file, setFile]                   = useState(null);
  const [preview, setPreview]             = useState(null);
  const [pdfUrl, setPdfUrl]               = useState(null);
  const [parsing, setParsing]             = useState(false);
  const [parsed, setParsed]               = useState([]);
  const [importing, setImporting]         = useState(false);
  const [msg, setMsg]                     = useState(null);
  const [detectedCurrency, setDetected]   = useState(null);
  const [bulkCurrency, setBulkCurrency]   = useState('');
  const fileRef                           = useRef();

  const applyBulkCurrency = (curr) => {
    if (!curr) return;
    setParsed(prev => prev.map(r => ({ ...r, currency: curr })));
  };

  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setParsed([]);
    setPreview(null);
    setPdfUrl(null);
    setDetected(null);
    setBulkCurrency('');

    if (f.name.toLowerCase().endsWith('.pdf')) {
      // Show PDF inline
      const url = URL.createObjectURL(f);
      setPdfUrl(url);
    } else {
      // Text/CSV preview (first 3000 chars)
      const reader = new FileReader();
      reader.onload = ev => setPreview((ev.target.result || '').slice(0, 3000));
      reader.readAsText(f);
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category_names', categories.map(c => c.name).join(','));

      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/finance/parse-statement', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Parse failed');
      const dc = data.detected_currency || 'INR';
      setDetected(dc);
      const txns = (data.transactions || []).map(t => ({ ...t, currency: t.currency || dc }));
      setParsed(txns);
      flash(`Found ${data.count} transactions. Review below and click Import.`);
    } catch (err) {
      flash(err.message, 'error');
    } finally {
      setParsing(false);
    }
  };

  const toggleParsedRow = (idx, field, value) => {
    setParsed(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeParsedRow = (idx) => setParsed(prev => prev.filter((_, i) => i !== idx));

  const handleImport = async () => {
    if (parsed.length === 0) return;
    setImporting(true);
    await onImport(parsed);
    setParsed([]);
    setFile(null);
    setPreview(null);
    setDetected(null);
    setBulkCurrency('');
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    if (fileRef.current) fileRef.current.value = '';
    flash(`${parsed.length} transactions imported!`);
    setImporting(false);
  };

  return (
    <div style={S.section}>
      <div style={S.sectionHead} onClick={() => setOpen(o => !o)}>
        <span style={S.sectionTitle}>Upload Bank Statement</span>
        <span style={{ color: '#666', fontSize: '13px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <>
          <Flash msg={msg} onDismiss={() => setMsg(null)} />

          {/* File picker */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.csv,.txt,.tsv"
              onChange={handleFile}
              style={{ color: '#ccc', fontSize: '13px' }}
            />
            {file && (
              <button style={S.btnPrimary} onClick={handleParse} disabled={parsing}>
                {parsing ? 'Parsing with AI…' : '🤖 Parse with AI'}
              </button>
            )}
          </div>

          {/* PDF preview */}
          {pdfUrl && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
                PDF Preview — personal details (account numbers, PAN, phone) are masked before AI processing
              </div>
              <iframe
                src={pdfUrl}
                title="Statement preview"
                style={{ width: '100%', height: '380px', border: '1px solid #333', borderRadius: '6px' }}
              />
            </div>
          )}

          {/* Text preview */}
          {preview && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
                File preview (first 3000 chars) — personal details masked before AI processing
              </div>
              <pre style={{
                background: '#12121e', border: '1px solid #2a2a3e', borderRadius: '6px',
                padding: '12px', fontSize: '11px', color: '#aaa', overflowX: 'auto',
                maxHeight: '220px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {preview}
              </pre>
            </div>
          )}

          {/* Parsed results review table */}
          {parsed.length > 0 && (
            <>
              {/* Detected currency banner */}
              {detectedCurrency && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '8px', background: '#0c2a1e', border: '1px solid #1a4a30', fontSize: '12px', color: '#86efac', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span>Detected currency: <strong>{detectedCurrency}</strong></span>
                  <button
                    style={{ background: '#1a4a30', border: '1px solid #4ade80', borderRadius: '5px', color: '#86efac', padding: '2px 10px', fontSize: '12px', cursor: 'pointer' }}
                    onClick={() => applyBulkCurrency(detectedCurrency)}
                  >
                    Apply to all rows
                  </button>
                </div>
              )}
              {/* Bulk currency change */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Set all to:</span>
                <select
                  value={bulkCurrency}
                  onChange={e => setBulkCurrency(e.target.value)}
                  style={S.select}
                >
                  <option value="">— pick —</option>
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
                <button
                  style={{ ...S.btnSecondary, opacity: bulkCurrency ? 1 : 0.5 }}
                  disabled={!bulkCurrency}
                  onClick={() => applyBulkCurrency(bulkCurrency)}
                >
                  Apply to all
                </button>
              </div>
              <div style={{ fontSize: '13px', color: '#ccc', marginBottom: '10px' }}>
                Review parsed transactions — miscellaneous rows are highlighted. Edit inline, remove unwanted, then Import.
              </div>
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>Description</th>
                      <th style={S.th}>Amount</th>
                      <th style={S.th}>Currency</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Category</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((row, i) => (
                      <tr key={i} style={row.is_misc ? S.trMisc : {}}>
                        <td style={S.td}>
                          <input type="date" style={{ ...S.input, width: '130px' }}
                            value={row.date} onChange={e => toggleParsedRow(i, 'date', e.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '220px' }}
                            value={row.description} onChange={e => toggleParsedRow(i, 'description', e.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input type="number" style={{ ...S.input, width: '100px' }}
                            value={row.amount} onChange={e => toggleParsedRow(i, 'amount', e.target.value)} />
                        </td>
                        <td style={S.td}>
                          <select style={S.select} value={row.currency || detectedCurrency || 'INR'}
                            onChange={e => toggleParsedRow(i, 'currency', e.target.value)}>
                            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>
                          <select style={S.select} value={row.type}
                            onChange={e => toggleParsedRow(i, 'type', e.target.value)}>
                            <option value="expense">Expense</option>
                            <option value="income">Income</option>
                          </select>
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '140px' }}
                            value={row.category_name}
                            onChange={e => {
                              toggleParsedRow(i, 'category_name', e.target.value);
                              toggleParsedRow(i, 'is_misc', e.target.value === 'Miscellaneous');
                            }} />
                          {row.is_misc && <span style={S.badgeMisc}>MISC</span>}
                        </td>
                        <td style={S.td}>
                          <button style={S.btnDanger} onClick={() => removeParsedRow(i)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
                <button style={S.btnPrimary} onClick={handleImport} disabled={importing}>
                  {importing ? 'Importing…' : `⬇ Import ${parsed.length} transactions`}
                </button>
                <button style={S.btnSecondary} onClick={() => { setParsed([]); setDetected(null); setBulkCurrency(''); }}>Clear</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Inline-editable transaction row ──────────────────────────────────────────

function TxRow({ tx, categories, onSave, onDelete }) {
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState({
    date: tx.date || '', description: tx.description || '',
    amount: tx.amount || '', currency: tx.currency || 'INR', type: tx.type || 'expense',
    category_id: tx.category_id || '', category_name: tx.category_name || '',
    notes: tx.notes || '',
  });
  const [saving, setSaving]     = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const filtered       = categories.filter(c => c.type === form.type);
  const filteredGlobal = filtered.filter(c => c.is_global);
  const filteredUser   = filtered.filter(c => !c.is_global);

  const handleSave = async () => {
    setSaving(true);
    const cat = categories.find(c => c.id === parseInt(form.category_id));
    await onSave(tx.id, {
      ...form,
      amount: parseFloat(form.amount),
      category_id: cat ? cat.id : null,
      category_name: cat ? cat.name : (form.category_name || 'Miscellaneous'),
      is_misc: !cat && (!form.category_name || form.category_name === 'Miscellaneous'),
    });
    setSaving(false);
    setEditing(false);
  };

  const cat = categories.find(c => c.id === tx.category_id);
  const dotColor = cat ? cat.color : '#607d8b';

  if (editing) {
    return (
      <tr style={tx.is_misc ? S.trMisc : {}}>
        <td style={S.td}><input type="date" style={{ ...S.input, width: '130px' }} value={form.date} onChange={e => set('date', e.target.value)} /></td>
        <td style={S.td}><input style={{ ...S.input, width: '220px' }} value={form.description} onChange={e => set('description', e.target.value)} /></td>
        <td style={S.td}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <input type="number" style={{ ...S.input, width: '90px' }} value={form.amount} onChange={e => set('amount', e.target.value)} />
            <select style={{ ...S.select, padding: '4px 6px' }} value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
        </td>
        <td style={S.td}>
          <select style={S.select} value={form.type} onChange={e => { set('type', e.target.value); set('category_id', ''); }}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
        </td>
        <td style={S.td}>
          <select style={S.select} value={form.category_id} onChange={e => set('category_id', e.target.value)}>
            <option value="">— Category —</option>
            {filteredUser.length > 0 && (
              <optgroup label="Your Categories">
                {filteredUser.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
            {filteredGlobal.length > 0 && (
              <optgroup label="Global Categories">
                {filteredGlobal.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
          </select>
        </td>
        <td style={S.td}><input style={{ ...S.input, width: '120px' }} placeholder="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} /></td>
        <td style={S.td}>
          <button style={{ ...S.btnPrimary, marginRight: '4px' }} onClick={handleSave} disabled={saving}>
            {saving ? '…' : '✓'}
          </button>
          <button style={S.btnSmall} onClick={() => setEditing(false)}>✕</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={tx.is_misc ? S.trMisc : {}} onDoubleClick={() => setEditing(true)}>
      <td style={S.td}>{tx.date}</td>
      <td style={S.td}>
        {tx.description}
        {tx.is_misc ? <span style={S.badgeMisc}>MISC</span> : null}
      </td>
      <td style={{ ...S.td, fontWeight: 600, color: tx.type === 'income' ? '#4caf50' : '#f44336' }}>
        {fmtAmount(tx.amount, tx.currency)}
      </td>
      <td style={S.td}>
        <span style={tx.type === 'income' ? S.badgeIncome : S.badgeExpense}>
          {tx.type.toUpperCase()}
        </span>
      </td>
      <td style={S.td}>
        {tx.category_name && (
          <>
            <span style={S.dot(dotColor)} />
            {tx.category_name}
          </>
        )}
      </td>
      <td style={{ ...S.td, color: '#666', fontSize: '12px' }}>{tx.notes || '—'}</td>
      <td style={S.td}>
        <button style={{ ...S.btnSmall, marginRight: '4px' }} onClick={() => setEditing(true)} title="Edit">✎</button>
        <button style={S.btnDanger} onClick={() => onDelete(tx.id)} title="Delete">✕</button>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ExpenseTracker() {
  const [transactions, setTransactions] = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [balances,     setBalances]     = useState([]);
  const [summary,      setSummary]      = useState({ total_income: 0, total_expense: 0, savings: 0 });
  const [loading,      setLoading]      = useState(true);
  const [msg,          setMsg]          = useState(null);

  const now  = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);   // 1-based
  const [typeFilter, setTypeFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [baseCurrency, setBaseCurrency] = useState(() => localStorage.getItem('baseCurrency') || 'INR');

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const loadTransactions = useCallback(() => {
    const qs = new URLSearchParams({ year, month });
    if (typeFilter !== 'all') qs.set('type', typeFilter);
    if (currencyFilter !== 'all') qs.set('currency', currencyFilter);
    authFetch(`/api/finance/transactions?${qs}`)
      .then(r => r.json())
      .then(d => setTransactions(d.transactions || []))
      .catch(() => {});
  }, [year, month, typeFilter, currencyFilter]);

  const loadSummary = useCallback(() => {
    const qs = new URLSearchParams({ year, month });
    if (currencyFilter !== 'all') qs.set('currency', currencyFilter);
    if (baseCurrency) qs.set('base_currency', baseCurrency);
    authFetch(`/api/finance/summary?${qs}`)
      .then(r => r.json())
      .then(d => setSummary(d))
      .catch(() => {});
  }, [year, month, currencyFilter, baseCurrency]);

  const loadCategories = useCallback(() => {
    authFetch('/api/finance/categories')
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => {});
  }, []);

  const loadBalances = useCallback(() => {
    authFetch('/api/finance/opening-balance')
      .then(r => r.json())
      .then(d => setBalances(d.balances || []))
      .catch(() => {});
  }, []);

  const refreshAll = useCallback(() => {
    setLoading(true);
    Promise.all([loadCategories(), loadBalances(), loadTransactions(), loadSummary()])
      .finally(() => setLoading(false));
  }, [loadCategories, loadBalances, loadTransactions, loadSummary]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const addCategory = async (form) => {
    try {
      const r = await authFetch('/api/finance/categories', {
        method: 'POST', body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadCategories();
      flash('Category added');
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Related transactions will be marked Miscellaneous.')) return;
    try {
      await authFetch(`/api/finance/categories/${id}`, { method: 'DELETE' });
      loadCategories();
      loadTransactions();
      flash('Category deleted');
    } catch (e) { flash(e.message, 'error'); }
  };

  const addTransaction = async (body) => {
    try {
      const r = await authFetch('/api/finance/transactions', {
        method: 'POST', body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadTransactions();
      loadSummary();
      flash('Transaction added');
    } catch (e) { flash(e.message, 'error'); }
  };

  const saveTransaction = async (id, body) => {
    try {
      const r = await authFetch(`/api/finance/transactions/${id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadTransactions();
      loadSummary();
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await authFetch(`/api/finance/transactions/${id}`, { method: 'DELETE' });
      loadTransactions();
      loadSummary();
      flash('Transaction deleted');
    } catch (e) { flash(e.message, 'error'); }
  };

  const saveBalance = async (form) => {
    try {
      const r = await authFetch('/api/finance/opening-balance', {
        method: 'POST', body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      loadBalances();
      flash('Balance saved');
    } catch (e) { flash(e.message, 'error'); }
  };

  const deleteBalance = async (id) => {
    if (!window.confirm('Remove this balance entry?')) return;
    try {
      await authFetch(`/api/finance/opening-balance/${id}`, { method: 'DELETE' });
      loadBalances();
      flash('Balance removed');
    } catch (e) { flash(e.message, 'error'); }
  };

  const importParsed = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      try {
        const cat = categories.find(c => c.name === row.category_name);
        const r = await authFetch('/api/finance/transactions', {
          method: 'POST',
          body: JSON.stringify({ ...row, category_id: cat ? cat.id : null }),
        });
        if (r.ok) ok++;
      } catch (_) {}
    }
    loadTransactions();
    loadSummary();
    flash(`${ok} of ${rows.length} transactions imported`);
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const openingBalance = balances.reduce((s, b) => s + parseFloat(b.balance || 0), 0);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Expense & Income Tracker</h1>
      <p style={S.subtitle}>Track your monthly cash flow. Double-click any row to edit inline.</p>

      <Flash msg={msg} onDismiss={() => setMsg(null)} />

      {/* Period filter */}
      <div style={S.filterRow}>
        <select style={S.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select style={S.select} value={year} onChange={e => setYear(Number(e.target.value))}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select style={S.select} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All types</option>
          <option value="income">Income only</option>
          <option value="expense">Expenses only</option>
        </select>
        <select style={S.select} value={currencyFilter} onChange={e => setCurrencyFilter(e.target.value)}>
          <option value="all">All currencies</option>
          {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} {c.symbol}</option>)}
        </select>
        <button style={S.btnSecondary} onClick={refreshAll}>↻ Refresh</button>
      </div>

      {/* Summary */}
      <SummaryCards
        summary={summary}
        openingBalance={openingBalance}
        currencyFilter={currencyFilter}
        baseCurrency={baseCurrency}
        setBaseCurrency={(c) => { setBaseCurrency(c); localStorage.setItem('baseCurrency', c); }}
      />

      {/* Bank balances */}
      <BalanceManager balances={balances} onSave={saveBalance} onDelete={deleteBalance} />

      {/* Categories */}
      <CategoryManager categories={categories} onAdd={addCategory} onDelete={deleteCategory} />

      {/* Add transaction */}
      <AddTransactionForm categories={categories} onAdd={addTransaction} />

      {/* Statement upload */}
      <StatementUploader categories={categories} onImport={importParsed} />

      {/* Transaction list */}
      <div style={S.section}>
        <div style={{ ...S.sectionHead, cursor: 'default' }}>
          <span style={S.sectionTitle}>
            Transactions — {MONTHS[month - 1]} {year}
            <span style={{ color: '#555', fontWeight: 400, fontSize: '13px', marginLeft: '8px' }}>
              ({transactions.length} records)
            </span>
          </span>
        </div>

        {loading && <div style={{ color: '#666', fontSize: '13px' }}>Loading…</div>}

        {!loading && transactions.length === 0 && (
          <div style={{ color: '#555', fontSize: '13px' }}>
            No transactions for this period. Add one above or upload a bank statement.
          </div>
        )}

        {transactions.length > 0 && (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Amount</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Category</th>
                  <th style={S.th}>Notes</th>
                  <th style={S.th}></th>
                  {/* Currency col handled inside TxRow */}
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <TxRow
                    key={tx.id}
                    tx={tx}
                    categories={categories}
                    onSave={saveTransaction}
                    onDelete={deleteTransaction}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
